import { MedicalExpertMapper } from "@/data/mapper/doctor/medical-expert-mapper";
import { safeMapToEntity } from "@/data/repo-Implement/safe-map-to-entity";
import { DoctorSource } from "@/data/source/doctor-source";
import { MedicalExpert } from "@/domain/entity/doctor/medical-expert-entity";
import { CreateDoctorPayload, DoctorRepository } from "@/domain/repository/doctor-repository";
import { SATError, SATResult } from "@sadtx/sat-error";

export class DoctorRepositoryImpl implements DoctorRepository {
  constructor(private readonly doctorSource: DoctorSource) {}

  async fetchDoctors(): Promise<SATResult<MedicalExpert[], SATError>> {
    const result = await this.doctorSource.fetchDoctors();
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      MedicalExpertMapper.toEntityList,
      "DoctorRepositoryImpl::fetchDoctors",
    );
  }

  async fetchChargeDoctors(): Promise<SATResult<MedicalExpert[], SATError>> {
    const result = await this.doctorSource.fetchChargeDoctors();
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      MedicalExpertMapper.toEntityList,
      "DoctorRepositoryImpl::fetchChargeDoctors",
    );
  }

  async fetchDoctor(uuid: string): Promise<SATResult<MedicalExpert, SATError>> {
    const result = await this.doctorSource.fetchDoctor(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      MedicalExpertMapper.toEntity,
      "DoctorRepositoryImpl::fetchDoctor",
    );
  }

  async createMedicalExpert(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.createMedicalExpert(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async createNurse(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.createNurse(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async createCro(payload: CreateDoctorPayload): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.createCro(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async updateDoctor(uuid: string, payload: Record<string, unknown>): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.updateDoctor(uuid, payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async deactivateDoctor(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.deactivateDoctor(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async activateDoctor(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.activateDoctor(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async resendAccountSetupEmail(uuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.resendAccountSetupEmail(uuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async updatePasswordExpiryDate(uuid: string, snUpdatedAt: string): Promise<SATResult<void, SATError>> {
    const result = await this.doctorSource.updatePasswordExpiryDate(uuid, snUpdatedAt);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }
}
